/**
 * Окрема продаюча сторінка промокоду: /promo/:slug
 *
 * Відкривається з /start CTA по промо-deeplink (t.me/<bot>?start=<slug>).
 * Окремий «ticket»-стиль (не клон розділу підписки): primary-акцент, рамка,
 * код-чип. Фон — спільний <AuraBackground /> (як на всіх сторінках). Закриття
 * нативною ✕ Telegram (свій BackNav прибрано, нативний BackButton ховаємо).
 */

import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuraBackground } from "@/components/AuraBackground";
import { StandaloneHeader } from "@/components/StandaloneHeader";
import { Icon, ScribbleUnderline } from "@/components/Icon";
import { CenteredError, Spinner } from "@/components/Spinner";
import { usePromoActivate, usePromoCheck } from "@/api/hooks";
import { SUBSCRIPTION_BENEFITS } from "@/lib/benefits";
import { formatAmount, ukrDaysWord } from "@/lib/utils";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";

export function PromoPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const check = usePromoCheck(slug, slug.length > 0);
  const activate = usePromoActivate();

  // Ховаємо нативний Telegram BackButton — закриття через ✕ (top-right).
  // Запамʼятовуємо slug: новий юзер спершу впреться в gate/онбординг, а
  // після нього Onboarding поверне його сюди (/promo/<slug>) для активації
  // — щоб промо-намір не губився (юзер 2026-06-09, 100% конверсія).
  // Запамʼятовуємо slug → банер у стрічці показується ДО фактичної оплати.
  // НЕ чистимо при заході на /promo (юзер 2026-06-10: перехід на сторінку
  // нічого не активує). pending_promo прибирає банер у стрічці, коли промо
  // стає недоступним (available=false = вже оплачено/використано).
  useEffect(() => {
    tg()?.BackButton?.hide?.();
    if (slug) localStorage.setItem("pending_promo", slug);
  }, [slug]);

  // Після оплати: ловимо активацію (промо стає redeemed → available=false)
  // і ведемо юзера на /subscription, де показується вікно «підписку
  // активовано». Інакше промо-сторінка лишалась би висіти зі старим станом
  // «рахунок створено» (юзер 2026-06-09). Поллимо поки є свіжий
  // pending_invoice саме для цього slug.
  useEffect(() => {
    if (!slug) return;
    const raw = localStorage.getItem("pending_invoice");
    if (!raw) return;
    let p: { tariff_slug?: string; started_at_before?: number } | null = null;
    try {
      p = JSON.parse(raw);
    } catch {
      return;
    }
    if (!p || p.tariff_slug !== `promo:${slug}`) return;
    if (Date.now() - (p.started_at_before ?? 0) > 15 * 60 * 1000) return;

    let stopped = false;
    let attempts = 0;
    const detect = async (): Promise<boolean> => {
      const r = await check.refetch();
      if (r.data && r.data.available === false) {
        navigate("/subscription", { replace: true });
        return true;
      }
      return false;
    };
    const iv = window.setInterval(async () => {
      attempts += 1;
      if (stopped) return;
      if (attempts > 30 || (await detect())) {
        clearInterval(iv);
      }
    }, 3000);
    // одразу перевіряємо при поверненні в апку (без очікування 3с)
    void detect();
    return () => {
      stopped = true;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleActivate = async () => {
    const webapp = tg();
    try {
      const result = await activate.mutateAsync(slug);
      localStorage.setItem(
        "pending_invoice",
        JSON.stringify({
          order_reference: result.order_reference,
          tariff_slug: `promo:${slug}`,
          started_at_before: Date.now(),
        }),
      );
      if (webapp?.openLink) webapp.openLink(result.purchase_url);
      else window.open(result.purchase_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      webapp?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(
        `Не вдалося активувати промокод: ${(e as Error).message}`,
      );
    }
  };

  if (check.isLoading) return <Spinner />;
  if (check.isError || !check.data) {
    return (
      <CenteredError
        message={
          check.error?.message ?? "Промокод не знайдено або вже неактивний"
        }
      />
    );
  }

  const promo = check.data;
  const amount = formatAmount(promo.amount);

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg">
      <AuraBackground />

      {/* Єдиний хедер для ВСІХ сторінок — логотип на тому ж місці, що й на
          головній (StandaloneHeader = topInset + 20). Справа «Меню». */}
      <StandaloneHeader
        right={
          <Link
            to="/"
            className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
          >
            Меню
            <Icon name="home" size={14} color="#FE6B04" />
          </Link>
        }
      />

      <div className="relative z-[1] px-5 pt-6 pb-12">
        <div className="space-y-4">
          {/* ===== Ticket ===== */}
          <div className="rounded-ios-hero overflow-hidden border border-primary/25 bg-surface shadow-ai-card">
            {/* Top band */}
            <div className="flex items-center justify-between bg-primary px-5 py-3">
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-white/90">
                секретний промокод
              </span>
              <span className="rounded-full bg-white/20 px-2.5 py-0.5 font-mono text-[0.6875rem] font-semibold text-white">
                {slug}
              </span>
            </div>

            {/* Body */}
            <div className="px-5 pt-5 pb-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div
                    className="font-display font-bold text-ink"
                    style={{
                      fontSize: "1.75rem",
                      lineHeight: 1.04,
                      letterSpacing: "-0.025em",
                    }}
                  >
                    {promo.days} {ukrDaysWord(promo.days)}
                  </div>
                  <div className="mt-1 whitespace-nowrap text-[0.84375rem] font-medium text-ink-dim">
                    повного доступу до inFLOW
                  </div>
                </div>

                <div className="shrink-0 text-right">
                  <span className="relative inline-block">
                    <span
                      className="font-display font-extrabold text-primary"
                      style={{ fontSize: "3.125rem", lineHeight: 0.9 }}
                    >
                      {amount} ₴
                    </span>
                    {/* w-full → підкреслення тягнеться рівно по ширині блоку
                        ціни; товщина фіксована (height у SVG не залежить
                        від width) — юзер 2026-06-09. */}
                    <ScribbleUnderline
                      stretch
                      className="absolute -bottom-2 left-0 w-full"
                    />
                  </span>
                </div>
              </div>

              {promo.available && promo.can_create_invoice ? (
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={activate.isPending}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-display text-[0.9375rem] font-semibold text-white shadow-card active:scale-[0.985] active:bg-primary-deep transition disabled:opacity-60"
                >
                  {activate.isPending ? (
                    "Створюємо рахунок…"
                  ) : (
                    <>
                      Активувати
                      <span className="text-[1.0625rem] leading-none">→</span>
                    </>
                  )}
                </button>
              ) : !promo.available ? (
                <div className="mt-5 rounded-xl bg-surface-soft px-3.5 py-2.5 text-[0.78125rem] text-ink-dim">
                  Цей промокод уже використано 🙌
                </div>
              ) : (
                <div className="mt-5 rounded-xl bg-surface-soft px-3.5 py-2.5 text-[0.78125rem] text-ink-dim">
                  {promo.has_pending_invoice
                    ? "Рахунок уже створено — він активний 15 хвилин. Сплати його, або за 15 хвилин зможеш створити новий рахунок для оплати 🙏"
                    : "Активація зараз недоступна."}
                </div>
              )}
            </div>
          </div>

          {/* ===== Перки (один стовпець, рівні відступи) ===== */}
          <div className="rounded-ios-card bg-dark p-5 text-white shadow-ai-card relative overflow-hidden">
            <div
              className="pointer-events-none absolute -bottom-10 -right-10 h-[9.375rem] w-[9.375rem] rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,161,53,0.35), rgba(255,161,53,0))",
              }}
            />
            <div className="relative">
              <div className="font-display text-[0.9375rem] font-bold">
                Все, що відкриває промокод{" "}
                <span className="text-primary">👇</span>
              </div>
              <div className="mt-3.5 space-y-3">
                {SUBSCRIPTION_BENEFITS.map((b) => (
                  <div key={b.title} className="flex items-center gap-3">
                    {/* Фіксована ширина + center → всі емодзі рівно в колонку */}
                    <span className="w-6 shrink-0 text-center text-[1.0625rem] leading-none">
                      {b.icon}
                    </span>
                    <span className="text-[0.8125rem] font-medium leading-tight text-white/85">
                      {b.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ===== Що таке inFLOW ===== */}
          <div className="rounded-ios-card bg-surface p-4 shadow-card-soft">
            <div className="font-display text-[0.90625rem] font-bold text-ink mb-1.5">
              Що таке inFLOW?
            </div>
            <p className="text-[0.84375rem] leading-relaxed text-ink-dim">
              Щодня знаходимо <b className="text-ink">400+ digital-вакансій</b> у
              сотнях Telegram-груп, по всьому Threads та на Freelancehunt — і{" "}
              <b className="text-ink">миттєво доставляємо</b> їх тобі.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
